import React, { useState, useEffect, useRef } from "react";
import styles from "./Buttons.module.css";

/**
 * SvgButton Component
 *
 * This component renders a button with an SVG icon and text. The SVG icon
 * can change its color on hover if the hoverEffect, iconColor, and hoverIconColor
 * props are provided.
 *
 * Props:
 * @param {string} svgIconPath - The path to the SVG file.
 * @param {string} text - The text to display inside the button.
 * @param {string} btnClass - Additional CSS classes for the button.
 * @param {string} iconColor - The default color of the SVG icon.
 * @param {string} hoverIconColor - The color of the SVG icon on hover.
 * @param {string} iconHeight - The height of the SVG icon.
 * @param {string} iconWidth - The width of the SVG icon.
 * @param {boolean} hoverEffect - Whether to apply hover effect on the SVG icon.
 * @param {object} restProps - Additional props to be passed to the button element.
 */

/*In future add btnBg, btnHoverBg/* 

*Usage
   <SvgButton
        svgIconPath="/resources/icons/thumbs-up-green.svg"
        iconHeight={24}
        iconWidth={24}
        iconColor="#000000"
        hoverIconColor="red"
        hoverEffect={true}
      />
      */

const SvgButton = ({
  svgIconPath,
  text,
  btnClass,
  iconColor,
  hoverIconColor,
  hoverBgColor = "#0a53a8",
  iconHeight,
  iconWidth,
  hoverEffect = false,
  style = {},
  ...restProps
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [svgContent, setSvgContent] = useState(null);
  const svgRef = useRef(null);

  // Fetch SVG content from the provided path
  useEffect(() => {
    if (svgIconPath) {
      fetch(svgIconPath)
        .then((response) => response.text())
        .then((svg) => {
          setSvgContent(svg);
        });
    }
  }, [svgIconPath]);

  useEffect(() => {
    if (svgRef.current && svgContent) {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
      const svgElement = svgDoc.documentElement;

      // Set SVG Heightt and Width
      if (iconHeight) svgElement.setAttribute("height", iconHeight);
      if (iconWidth) svgElement.setAttribute("width", iconWidth);

      // Hover state colors for SVG
      if (hoverEffect && iconColor && hoverIconColor) {
        const paths = svgDoc.querySelectorAll("path");
        paths.forEach((path) =>
          path.setAttribute("fill", isHovered ? hoverIconColor : iconColor)
        );
      }

      // Update the inner HTML of the ref with the new SVG content
      svgRef.current.innerHTML = new XMLSerializer().serializeToString(
        svgElement
      );
    }
  }, [svgContent, isHovered, iconColor, hoverIconColor]);

  const handleMouseEnter = () => {
    if (hoverEffect) setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (hoverEffect) setIsHovered(false);
  };

  return (
    <button
      className={`${styles.svgButton} ${btnClass}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        backgroundColor: isHovered ? hoverBgColor : "transparent",
        color: isHovered ? "#ffffff" : "#3b5998",
        ...style,
      }}
      {...restProps}
    >
      <div ref={svgRef}></div>
      <span>{text}</span>
    </button>
  );
};

export default SvgButton;
